"use client";
import React, { useState } from "react";
import Image from "next/image";
import {
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Container,
  Tab,
  Typography,
  useMediaQuery,
} from "@mui/material";
import Grid from "@mui/material/Grid2";

import { toKebabCase, toTitleCase } from "@/utils/helpers";
import { ReadMoreButton } from "../cruise-results/itinerary/CruiseFacilities";
import { ReadMoreDrawer } from "../cruise-results/itinerary/ItineraryHero";
import { MUITabs } from "@/components/common/MUITabs";

export const newDestinationListOcean = [
  {
    name: "Mediterranean",
    page_url: "mediterranean-cruises",
  },
  {
    name: "Caribbean",
    page_url: "caribbean-cruises",
  },
  {
    name: "Transatlantic",
    page_url: "transatlantic-cruises",
  },
  {
    name: "Alaska",
    page_url: "alaska-cruises",
  },
  {
    name: "Panama Canal",
    page_url: "panama-canal-cruises",
  },
  {
    name: "Middle East",
    page_url: "middle-east-cruises",
  },
  {
    name: "Pacific",
    page_url: "pacific-cruises",
  },
  {
    name: "Indian Ocean",
    page_url: "indian-ocean-cruises",
  },
  {
    name: "Asia", // Far East
    page_url: "asia-cruises",
  },
  {
    name: "Africa",
    page_url: "africa-cruises",
  },
  {
    name: "Antarctica",
    page_url: "antarctica-cruises",
  },
  {
    name: "Arctic",
    page_url: "arctic-cruises",
  },
  {
    name: "West Coast US",
    page_url: "west-coast-us-cruises",
  },
  {
    name: "World wide",
    page_url: "World-wide-cruises",
  },
  {
    name: "Atlantic Islands",
    page_url: "atlantic-islands-cruises",
  },
];

export const newDestinationListRiver = [
  {
    name: "Danube",
    page_url: "danube-cruises",
  },
  {
    name: "Rhine",
    page_url: "rhine-cruises",
  },
  {
    name: "Seine",
    page_url: "seine-cruises",
  },
  {
    name: "Douro",
    page_url: "douro-cruises",
  },
  {
    name: "Dutch Waterways",
    page_url: "dutch-cruises",
  },
  {
    name: "Rhone",
    page_url: "rhone-cruises",
  },
  {
    name: "Main",
    page_url: "main-cruises",
  },
  {
    name: "Moselle",
    page_url: "moselle-cruises",
  },
  {
    name: "Nile",
    page_url: "nile-cruises",
  },
  {
    name: "Ganges",
    page_url: "ganges-cruises",
  },
  {
    name: "Mekong",
    page_url: "mekong-cruises",
  },
  {
    name: "Mississippi",
    page_url: "mississippi-cruises",
  },
  {
    name: "Amazon",
    page_url: "amazon-cruises",
  },
  {
    name: "Chobe",
    page_url: "chobe-cruises",
  },
  {
    name: "Elbe",
    page_url: "elbe-cruises",
  },
  {
    name: "Saone",
    page_url: "saone-cruises",
  },
  {
    name: "Po",
    page_url: "po-cruises",
  },
];

export const destinationList = [...newDestinationListOcean, ...newDestinationListRiver];

const AllDestinations = () => {
  const [isReadMore, setIsReadMore] = useState(false);
  const lessThanMD = useMediaQuery("(max-width: 900px)");

  const [loading, setLoading] = useState(false);
  const [loadingDestination, setLoadingDestination] = useState({ name: "", page_url: "" });

  const handleDestinationClick = (destination: { name: string; page_url: string }) => {
    setLoading(true);
    setLoadingDestination(destination);
    const url = `/cruise-destinations/${toKebabCase(destination.page_url)}`;
    window.location.href = url;
  };

  const [selectedTab, setSelectedTab] = useState<string>("ocean");
  const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
    setSelectedTab(newValue);
  };

  return (
    <>
      <Box sx={{ width: "100%", pt: "14px", position: "relative", bgcolor: "#edf1ff" }}>
        <Container>
          <Typography
            variant="h1"
            align="center"
            gutterBottom
            sx={{
              fontSize: { xs: "2rem", md: "3rem" },
              mt: "16px",
              color: "#060273",
              textAlign: "center",
            }}
          >
            Choose the Cruise Destinations That Call to You
            {/* Set sail to the best places to cruise with zero hassle! */}
          </Typography>
          <Typography
            variant="subtitle2"
            gutterBottom
            sx={{
              mt: { xs: "-0.7rem", md: "-1.2rem" },
              mb: "20px",
              color: "#060273",
              textAlign: "center",
            }}
          >
            Explore the World&apos;s Most Breathtaking Cruise Destinations
          </Typography>

          <Box
            sx={{
              position: "relative",
              mb: "1rem",
            }}
          >
            <Typography
              sx={{
                overflow: { xs: "hidden" },
                WebkitLineClamp: { xs: 5, md: "unset" },
                display: "-webkit-box",
                WebkitBoxOrient: "vertical",
                textOverflow: "ellipsis",
                lineHeight: "25px",
                color: "#222",
              }}
            >
              Imagine a world where the sky is the limit as you sail across it. With so many
              navigable waterways in every corner of the world, it&apos;s tempting to daydream about
              the best places to cruise, faraway destinations or discovering hidden treasures in
              your nation. Regardless of your desired destination, a ship can transport you there.
              Experts in the cruise business, we&apos;ve compiled the top cruise routes and those on
              board have helped us narrow down the most desirable itineraries for you to experience.
            </Typography>
            {lessThanMD && (
              <ReadMoreButton
                isReadMore={isReadMore}
                onClick={() => setIsReadMore((prev) => !prev)}
              />
            )}
          </Box>
        </Container>
      </Box>

      <Container>
        <MUITabs
          stringValue={"ocean"}
          onChangeString={handleTabChange}
          sx={{
            mt: "1rem",
            mb: "2rem",
            ".MuiTabs-list.MuiTabs-flexContainer": {
              justifyContent: "center",
            },
          }}
        >
          {["ocean", "river"]?.map((item, index) => (
            <Tab
              key={index}
              iconPosition="start"
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "10px",
                bgcolor: "#fff !important",
                color: "#060273 !important",
                ...(selectedTab === item && {
                  bgcolor: "#060273 !important",
                  color: "#fff !important",
                }),
                minWidth: "220px",
                p: "2px 11px 2px",
              }}
              value={item}
              label={
                <Box
                  sx={{
                    textTransform: "capitalize",
                    fontWeight: 600,
                    fontSize: { xs: "15px", md: "20px" },
                    ml: { xs: "6px", md: "0px" },
                  }}
                >
                  {item} Cruises
                </Box>
              }
              onClick={() => {
                setSelectedTab(item);
              }}
            />
          ))}
        </MUITabs>

        <Grid container size={{ xs: 12 }} spacing={2} mt={{ xs: "2rem", md: "1rem" }}>
          {(selectedTab == "ocean" ? newDestinationListOcean : newDestinationListRiver).map(
            (dest, index) => (
              <Grid key={index} size={{ xs: 6, sm: 6, md: 4 }}>
                <Box
                  component="a"
                  href={`/cruise-destinations/${toKebabCase(dest.page_url)}`}
                  onClick={() => handleDestinationClick(dest)}
                  sx={{ cursor: "pointer" }}
                >
                  <Box
                    sx={{
                      position: "relative",
                      width: "100%",
                      height: { xs: "200px", md: "300px" },
                      border: "2px solid #fff",
                      boxShadow: "0px 0px 14px rgba(0,0,0,0.3)",
                      borderRadius: "15px",
                      overflow: "hidden",
                      p: "1rem",
                      transition: "all 0.5s",
                      "&:hover": {
                        border: "2px solid #5900FF",
                      },
                    }}
                  >
                    {loading && loadingDestination?.name === dest.name ? (
                      <Backdrop
                        open={true}
                        sx={{
                          position: "absolute",
                          zIndex: 20,
                          color: "#fff",
                          backgroundColor: "rgba(0, 0, 0, 0.5)",
                          borderRadius: 1,
                        }}
                      >
                        <CircularProgress color="inherit" />
                      </Backdrop>
                    ) : null}

                    <Image
                      src={`/images/destination-images/${toKebabCase(dest.name)}-cruises.jpg`}
                      alt={dest.name}
                      fill
                      style={{
                        objectFit: "cover",
                      }}
                      loader={({ src }) => src}
                      unoptimized
                    />

                    <Box
                      sx={{
                        top: { xs: "1rem", md: "1rem" },
                        left: { xs: "1rem", md: "1rem" },
                        position: "absolute",
                        zIndex: 12,
                        pointerEvents: "none",
                        color: "#fff",
                      }}
                    >
                      <Typography
                        variant="h3"
                        sx={{
                          fontSize: { xs: "1.2rem", sm: "2rem", md: "2rem" },
                          fontWeight: 900,
                          textShadow: "0px 0px 10px #55555577, 2px 2px 0px #3933E6",
                        }}
                      >
                        {toTitleCase(dest.name)}
                      </Typography>
                    </Box>

                    <Button
                      sx={{
                        display: { xs: "none", sm: "block" },
                        textTransform: "initial",
                        fontWeight: 500,
                        p: "6px 12px",
                        bgcolor: "primary.main",
                        color: "#fff",
                        gap: "6px",
                        bottom: { xs: "1rem", md: "1rem" },
                        right: { xs: "1rem", md: "1rem" },
                        position: "absolute",
                        "&:hover": {
                          bgcolor: "#5900FF",
                          color: "#fff",
                        },
                      }}
                    >
                      Explore Destination
                    </Button>
                  </Box>
                </Box>
              </Grid>
            ),
          )}
        </Grid>

        <ReadMoreDrawer
          isReadMore={isReadMore}
          onClose={() => setIsReadMore(false)}
          title="Destination"
        >
          <Typography
            sx={{
              lineHeight: "25px",
            }}
          >
            There&apos;s something magical about the open sea — a journey where the destination
            changes, but the sense of wonder stays constant. Cruising offers a seamless way to
            explore the world&apos;s beauty, diversity, and hidden treasures, all from the comfort
            of a luxurious floating home.
            <br />
            Imagine waking up each day to a new skyline, a fresh breeze, and the promise of
            discovery. From serene shores to vibrant cities, every stop brings a new chapter to your
            travel story. The landscapes shift, cultures unfold, and experiences enrich your soul —
            all without the stress of packing and unpacking.
            <br />
            Whether you&apos;re seeking relaxation, adventure, culture, or connection, cruising
            opens the door to it all. With thoughtfully crafted itineraries, exceptional service,
            and endless horizons, each voyage is more than a holiday — it&apos;s a moving
            experience.
          </Typography>
        </ReadMoreDrawer>
      </Container>

      {/* <Box>
        <Container>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", xl: "row" },
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: { xs: "1rem", md: "1.5rem", xl: "1rem" },
              mt: { xs: "0rem", md: "2rem", xl: "0rem" },
              mb: "0rem",
            }}
          >
            <Box sx={{}}>
              <Box
                sx={{
                  color: "#fff",
                  fontSize: "1rem",
                  fontWeight: 600,
                  mb: "10px",
                  mt: { xs: "10px", xl: "0px" },
                  fontFamily: "'blauerNue', 'blauerNue Fallback'",
                  textAlign: { xs: "center", xl: "left" },
                }}
              >
                Follow Us for Exclusive Updates!
              </Box>

              <Box>
                <SocialIcons sx={{ display: "flex", justifyContent: "center" }} />
              </Box>
            </Box>

            <Box sx={{ display: { xs: "none", md: "block" } }}>
              <Box
                sx={{
                  fontFamily: "'blauerNue', 'blauerNue Fallback'",
                  fontSize: "14px",
                  color: "#fff",
                  fontWeight: 400,
                  textAlign: { xs: "center", xl: "right" },
                  a: {
                    color: "#ff1184",
                    svg: {
                      mb: "-6px",
                      width: "21px",
                      height: "21px",
                      ml: "4px",
                    },
                  },
                }}
              >
                We offer flexible ways to pay for your dream cruise –{" "}
                <Link href="/payment">
                  Explore Payment Options!
                  <OpenTabIcon />
                </Link>
              </Box>

              <Box
                sx={{
                  position: "relative",
                  display: "flex",
                  justifyContent: "center",
                  textAlign: "center",
                  mt: "10px",
                  mb: "16px",
                  width: { xs: "100%", xl: "800px" },
                  img: {
                    objectFit: "contain",
                    width: "100%",
                    maxWidth: { xs: "100%", md: "900px", xl: "1200px" },
                    height: "auto",
                  },
                }}
              >
                <Image
                  src="/images/home/new-design/payment-icons-densed.png"
                  alt="Payment Icons"
                  width={800}
                  height={60}
                  loader={({ src }) => src}
                />
              </Box>
            </Box>
          </Box>
        </Container>
        <Box
          sx={{
            width: { xs: "100%", md: "calc(100% + 0rem)" },
            height: { xs: "150px", sm: "120px", md: "100px" },
            bgcolor: "#000c37",
            mt: { xs: "-145px", sm: "-90px", md: "calc(-110px + 0rem)" },
            ml: { xs: "0px", md: "0rem" },
            display: { xs: "none", sm: "block" },
            zIndex: -1,
            // borderRadius: "15px",
          }}
        ></Box>
      </Box> */}
    </>
  );
};
export default AllDestinations;
